// Anveshan — Home page (bilingual EN/HI)
// Reuses existing Layout (navigation, language switcher, auth). Design: cream bg,
// forest-green #2C4A3B accents, terracotta #D95A41 highlights.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import {
  Heart, Briefcase, Stethoscope, GraduationCap, ShoppingBag, Scales,
  Confetti, HandHeart, ArrowRight, Users, Target, Compass, Sparkle,
  Lightbulb, UsersThree, Globe, Buildings, ChatCircleText, CaretDown,
} from "@phosphor-icons/react";

const CREAM = "#F9F6F0";
const FOREST = "#2C4A3B";
const TERRA = "#D95A41";
const GOLD = "#D49A36";
const INK = "#1C1B1A";

const t = (lang, en, hi) => (lang === "hi" ? hi : en);

// ---------- content map (keeps the page compact and translatable) ----------
const SERVICES = [
  { to: "/matrimony",   icon: Heart,       en: ["Matrimony",   "Find a life partner within your community with verified profiles and private-by-default photos."], hi: ["विवाह",        "सत्यापित प्रोफ़ाइल और निजी फ़ोटो के साथ अपने समुदाय में जीवनसाथी खोजें।"] },
  { to: "/jobs",        icon: Briefcase,   en: ["Jobs",        "Two-way skill-based matching between candidates and verified employers."],                           hi: ["रोज़गार",       "सत्यापित भर्तीकर्ताओं और उम्मीदवारों के बीच कौशल-आधारित मिलान।"] },
  { to: "/doctors",     icon: Stethoscope, en: ["Doctors",     "Chat with community doctors, book clinic visits, track follow-ups."],                                hi: ["डॉक्टर",       "समुदाय डॉक्टरों से परामर्श लें और अपॉइंटमेंट बुक करें।"] },
  { to: "/training",    icon: GraduationCap, en: ["Training", "Free bite-sized courses across skill tracks, with certificates."],                                    hi: ["प्रशिक्षण",      "कौशल पाठ्यक्रमों के साथ निःशुल्क प्रशिक्षण और प्रमाणपत्र।"] },
  { to: "/marketplace", icon: ShoppingBag, en: ["Marketplace", "Buy and sell trusted second-hand goods — cash on delivery or direct enquiry."],                      hi: ["बाज़ार",       "भरोसेमंद उत्पादों की खरीद-बिक्री — कैश ऑन डिलीवरी।"] },
  { to: "/lawyers",     icon: Scales,      en: ["Lawyers",     "Reach verified lawyers for family, property, labour and civil matters."],                            hi: ["वक़ील",        "पारिवारिक, संपत्ति और नागरिक मामलों के लिए वक़ीलों से संपर्क करें।"] },
  { to: "/events",      icon: Confetti,    en: ["Events",      "Discover community events, buy tickets, scan QR passes at the gate."],                               hi: ["आयोजन",        "समुदाय के आयोजन खोजें और टिकट प्राप्त करें।"] },
  { to: "/support",     icon: HandHeart,   en: ["Support",     "Community-funded medical and financial aid for those who need it most."],                            hi: ["सहायता",       "ज़रूरतमंदों के लिए समुदाय-वित्तपोषित सहायता।"] },
];

const MISSION = [
  { icon: UsersThree, en: ["Connect People",                 "Help people find each other and grow together."],      hi: ["लोगों को जोड़ें",             "लोगों को मिलने और साथ बढ़ने में मदद करें।"] },
  { icon: Target,     en: ["Create Opportunities",            "Open doors for work, learning and growth."],           hi: ["अवसर पैदा करें",             "काम और शिक्षा के द्वार खोलें।"] },
  { icon: GraduationCap, en: ["Promote Education & Skills",   "Skills programs for every age and interest."],         hi: ["शिक्षा और कौशल",             "हर आयु के लिए कौशल कार्यक्रम।"] },
  { icon: Stethoscope,en: ["Professional Guidance",           "Doctors and lawyers, respectfully accessible."],        hi: ["पेशेवर मार्गदर्शन",            "डॉक्टर और वक़ील, सम्मानपूर्वक उपलब्ध।"] },
  { icon: Buildings,  en: ["Support Entrepreneurship",        "Give small businesses a trusted shelf."],              hi: ["उद्यमिता को बढ़ावा",         "छोटे व्यवसायों को विश्वसनीय मंच।"] },
  { icon: Heart,      en: ["Social Responsibility",            "Make giving back easy and transparent."],              hi: ["सामाजिक ज़िम्मेदारी",          "दान को आसान और पारदर्शी बनाएँ।"] },
  { icon: Compass,    en: ["Empower Communities",              "A single, trusted home for the samaj."],               hi: ["समुदाय का सशक्तिकरण",         "समाज के लिए एक विश्वसनीय मंच।"] },
];

const CHALLENGES = [
  { en: "Lack of awareness of employment opportunities",             hi: "रोज़गार अवसरों की जानकारी का अभाव" },
  { en: "Difficulty identifying suitable training programs",         hi: "उपयुक्त प्रशिक्षण खोजने में कठिनाई" },
  { en: "Limited access to qualified professional guidance",         hi: "योग्य पेशेवर मार्गदर्शन तक सीमित पहुँच" },
  { en: "Difficulty connecting with trustworthy people and services", hi: "भरोसेमंद लोगों और सेवाओं से जुड़ने में कठिनाई" },
  { en: "Limited visibility for local businesses and talent",         hi: "स्थानीय व्यवसायों और प्रतिभा की सीमित दृश्यता" },
  { en: "Need for community cooperation and social support",          hi: "समुदाय सहयोग और सामाजिक सहायता की आवश्यकता" },
];

const HOW_STEPS = [
  { en: "Join Anveshan",                      hi: "अन्वेषण से जुड़ें" },
  { en: "Create your profile",                hi: "अपनी प्रोफ़ाइल बनाएँ" },
  { en: "Explore relevant services",          hi: "सेवाओं का अन्वेषण करें" },
  { en: "Connect with people or professionals", hi: "लोगों और पेशेवरों से जुड़ें" },
  { en: "Access opportunities and support",    hi: "अवसरों और सहायता तक पहुँचें" },
  { en: "Contribute to community development", hi: "समुदाय विकास में योगदान दें" },
];

const WHO = [
  { en: "Students",             hi: "छात्र" },
  { en: "Job seekers",          hi: "रोज़गार चाहने वाले" },
  { en: "Employers",            hi: "भर्तीकर्ता" },
  { en: "Professionals",         hi: "पेशेवर" },
  { en: "Entrepreneurs",        hi: "उद्यमी" },
  { en: "Families",             hi: "परिवार" },
  { en: "Volunteers",            hi: "स्वयंसेवक" },
  { en: "Training providers",    hi: "प्रशिक्षण प्रदाता" },
  { en: "Community organizations", hi: "समुदाय संगठन" },
];

const STAT_LABELS = [
  { key: "members",             en: "Registered members",      hi: "पंजीकृत सदस्य" },
  { key: "jobs_published",       en: "Jobs currently live",     hi: "सक्रिय नौकरियाँ" },
  { key: "training_programs",    en: "Training programs",       hi: "प्रशिक्षण कार्यक्रम" },
  { key: "upcoming_events",      en: "Upcoming events",         hi: "आगामी आयोजन" },
  { key: "matrimony_profiles",   en: "Matrimony profiles",       hi: "विवाह प्रोफ़ाइलें" },
  { key: "marketplace_listings", en: "Marketplace listings",   hi: "बाज़ार सूचियाँ" },
  { key: "verified_doctors",     en: "Verified doctors",        hi: "सत्यापित डॉक्टर" },
  { key: "verified_lawyers",     en: "Verified lawyers",        hi: "सत्यापित वक़ील" },
];

// ---------- Home ----------
export default function Home() {
  const { lang } = useLang();
  const servicesRef = useRef(null);
  const scrollToServices = () => servicesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  useEffect(() => {
    document.title = lang === "hi" ? "अन्वेषण — समाज का उत्थान" : "Anveshan — Empowering Society";
    const desc = lang === "hi"
      ? "अन्वेषण — एक समुदाय-केंद्रित मंच जो रोज़गार, शिक्षा, स्वास्थ्य, विवाह, व्यवसाय, कानूनी सलाह और सामाजिक सहायता के अवसर जोड़ता है।"
      : "Anveshan is a community-focused platform that connects people with opportunities in employment, education, healthcare, relationships, commerce, legal guidance and social support.";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = desc;
  }, [lang]);
  return (
    <main data-testid="home-page" style={{ background: CREAM }}>
      <FreeBanner lang={lang} />
      <Hero lang={lang} onExplore={scrollToServices} />
      <About lang={lang} />
      <Motive lang={lang} />
      <VisionMission lang={lang} />
      <section ref={servicesRef}><Services lang={lang} /></section>
      <PartnersStrip lang={lang} />
      <HowItWorks lang={lang} />
      <WhoCanJoin lang={lang} />
      <ImpactStats lang={lang} />
      <JoinMovement lang={lang} />
    </main>
  );
}

// ---------- Free-for-everyone banner ----------
function FreeBanner({ lang }) {
  return (
    <div className="w-full" style={{ background: FOREST }} data-testid="free-banner">
      <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-center gap-2 text-xs sm:text-sm text-[#F9F6F0] text-center">
        <Sparkle size={13} weight="fill" style={{ color: GOLD }} />
        <span className="font-semibold">
          {t(lang,
            "Anveshan is 100% free for everyone. Buyers and sellers transact directly — we never collect, hold or settle payments.",
            "अन्वेषण पूर्णतः निःशुल्क है। लेन-देन सीधे खरीदार और विक्रेता के बीच होता है — हम भुगतान एकत्र, धारण या निपटान नहीं करते।")}
        </span>
      </div>
    </div>
  );
}

// ---------- Hero ----------
function Hero({ lang, onExplore }) {
  return (
    <section className="relative overflow-hidden" aria-labelledby="hero-head">
      <div className="max-w-7xl mx-auto px-6 pt-14 pb-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 relative z-10">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs tracking-widest uppercase font-bold"
               style={{ background: `${TERRA}14`, color: TERRA }} data-testid="hero-chip">
            <Sparkle size={11} weight="fill" /> {t(lang, "Anveshan · अन्वेषण", "अन्वेषण · Anveshan")}
          </div>
          <h1 id="hero-head" className="font-heading text-5xl sm:text-6xl lg:text-7xl mt-4 leading-[0.95] tracking-tight" style={{ color: INK }}>
            {t(lang,
              <>Together we <em className="not-italic" style={{ color: TERRA }}>explore</em>.<br />Together we grow.</>,
              <>एक सशक्त <em className="not-italic" style={{ color: TERRA }}>समाज</em><br />की ओर।</>
            )}
          </h1>
          <p className="mt-5 text-lg leading-relaxed max-w-xl" style={{ color: "#5C564D" }}>
            {t(lang,
              "Anveshan is a community-focused digital platform connecting individuals, families, professionals, businesses and social organizations with opportunities in employment, education, healthcare, relationships, commerce, legal guidance and social support.",
              "अन्वेषण एक समुदाय-केंद्रित डिजिटल मंच है जो व्यक्तियों, परिवारों, पेशेवरों, व्यवसायों और सामाजिक संगठनों को रोज़गार, शिक्षा, स्वास्थ्य, रिश्तों, व्यापार, कानूनी सलाह और सामाजिक सहायता के अवसरों से जोड़ता है।")}
          </p>
          <p className="mt-3 text-sm italic" style={{ color: FOREST }}>
            {t(lang,
              "Discovering potential · Creating opportunities · Empowering society.",
              "संभावनाओं की खोज · अवसरों का सृजन · समाज का उत्थान।")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" data-testid="hero-join" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02]" style={{ background: TERRA }}>
              {t(lang, "Join Anveshan", "अन्वेषण से जुड़ें")} <ArrowRight size={16} weight="bold" />
            </Link>
            <button onClick={onExplore} data-testid="hero-explore" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold border-2 transition-colors" style={{ borderColor: FOREST, color: FOREST }}>
              {t(lang, "Explore services", "सेवाएँ देखें")} <CaretDown size={14} weight="bold" />
            </button>
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-xl" style={{ background: `linear-gradient(135deg, ${FOREST} 0%, #1F3528 100%)` }}>
            <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: `radial-gradient(circle at 20% 20%, ${GOLD}66 0%, transparent 40%), radial-gradient(circle at 80% 70%, ${TERRA}55 0%, transparent 40%)`,
            }} />
            <div className="absolute inset-0 flex flex-col justify-end p-7 text-[#F9F6F0]">
              <div className="flex -space-x-3 mb-4">
                {[Heart, Briefcase, Stethoscope, GraduationCap, ShoppingBag, Scales].map((Ic, i) => (
                  <div key={i} className="w-11 h-11 rounded-full flex items-center justify-center border-2" style={{ background: CREAM, borderColor: CREAM, color: FOREST }}>
                    <Ic size={18} weight="duotone" />
                  </div>
                ))}
              </div>
              <div className="text-xs tracking-widest uppercase opacity-80">{t(lang, "One trusted platform", "एक विश्वसनीय मंच")}</div>
              <div className="font-heading text-3xl leading-tight mt-1">
                {t(lang, "Eight services. One community.", "आठ सेवाएँ। एक समाज।")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- About ----------
function About({ lang }) {
  return (
    <section className="py-20" style={{ background: "#FDFBF6" }} aria-labelledby="about-head">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: TERRA }}>{t(lang, "About", "परिचय")}</div>
        <h2 id="about-head" className="font-heading text-4xl sm:text-5xl mt-2 tracking-tight" style={{ color: FOREST }}>
          {t(lang, "What is Anveshan?", "अन्वेषण क्या है?")}
        </h2>
        <div className="mt-6 space-y-4 text-lg leading-relaxed" style={{ color: "#2E2A26" }}>
          <p>{t(lang,
            "Anveshan is an initiative to make useful information, essential services, professional expertise and opportunities easier to discover and access. It encourages people to share knowledge, develop skills, find work, build meaningful relationships and support one another.",
            "अन्वेषण एक पहल है जो उपयोगी जानकारी, आवश्यक सेवाओं, पेशेवर विशेषज्ञता और अवसरों को खोजने और उन तक पहुँचने को आसान बनाती है। यह लोगों को ज्ञान साझा करने, कौशल विकसित करने, काम ढूँढने, सार्थक रिश्ते बनाने और एक-दूसरे का सहयोग करने के लिए प्रोत्साहित करती है।")}</p>
          <p>{t(lang,
            "The platform seeks to reduce the gap between those who need opportunities and those who can provide them.",
            "यह मंच उन लोगों के बीच की दूरी कम करना चाहता है जिन्हें अवसरों की आवश्यकता है और जो उन्हें प्रदान कर सकते हैं।")}</p>
        </div>
      </div>
    </section>
  );
}

// ---------- Motive (challenges) ----------
function Motive({ lang }) {
  return (
    <section className="py-20" aria-labelledby="motive-head">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-3xl">
          <div className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: TERRA }}>{t(lang, "The motive", "कारण")}</div>
          <h2 id="motive-head" className="font-heading text-4xl sm:text-5xl mt-2 tracking-tight" style={{ color: FOREST }}>
            {t(lang, "Why Anveshan?", "अन्वेषण क्यों?")}
          </h2>
          <p className="mt-4 text-lg" style={{ color: "#5C564D" }}>
            {t(lang,
              "Across urban and rural communities, people face the same recurring gaps:",
              "शहरी और ग्रामीण समुदायों में लोग समान चुनौतियों का सामना करते हैं:")}
          </p>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="motive-list">
          {CHALLENGES.map((c, i) => (
            <div key={i} className="rounded-2xl p-5 border bg-white" style={{ borderColor: "#E5D9CC" }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${TERRA}18`, color: TERRA }}>
                <Lightbulb size={18} weight="duotone" />
              </div>
              <div className="mt-3 text-sm leading-relaxed" style={{ color: INK }}>{t(lang, c.en, c.hi)}</div>
            </div>
          ))}
        </div>
        <p className="mt-8 text-base leading-relaxed max-w-3xl" style={{ color: "#2E2A26" }}>
          {t(lang,
            "Anveshan aims to bridge these gaps through a single, trusted platform — accessible to urban and rural communities alike.",
            "अन्वेषण एक विश्वसनीय मंच के माध्यम से इन अंतरालों को पाटने का प्रयास करता है — शहरी और ग्रामीण दोनों समुदायों के लिए सुलभ।")}
        </p>
      </div>
    </section>
  );
}

// ---------- Vision + Mission ----------
function VisionMission({ lang }) {
  return (
    <section className="py-20" style={{ background: FOREST }} aria-labelledby="vision-head">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-10 text-[#F3EDE1]">
        <div className="lg:col-span-1">
          <div className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: GOLD }}>{t(lang, "Our vision", "हमारी दृष्टि")}</div>
          <h2 id="vision-head" className="font-heading text-4xl sm:text-5xl mt-2 tracking-tight">
            {t(lang, "A society that discovers itself.", "एक ऐसा समाज जो स्वयं को खोजे।")}
          </h2>
          <p className="mt-4 text-base leading-relaxed opacity-90">
            {t(lang,
              "A connected, informed, skilled, self-reliant and supportive society — where people can discover opportunities and participate in community development.",
              "एक जुड़ा हुआ, सूचित, कुशल, आत्मनिर्भर और सहायक समाज — जहाँ लोग अवसरों को खोज सकें और समुदाय विकास में भाग ले सकें।")}
          </p>
        </div>
        <div className="lg:col-span-2">
          <div className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: GOLD }}>{t(lang, "Our mission", "हमारा लक्ष्य")}</div>
          <h3 className="font-heading text-3xl mt-2">{t(lang, "Seven commitments to our samaj.", "समाज के लिए सात प्रतिबद्धताएँ।")}</h3>
          <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="mission-list">
            {MISSION.map((m, i) => {
              const Icon = m.icon;
              const [en, enb] = m.en; const [hi, hib] = m.hi;
              return (
                <div key={i} className="rounded-2xl p-4 flex items-start gap-3" style={{ background: "rgba(255,255,255,0.07)" }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: GOLD, color: INK }}><Icon size={18} weight="duotone" /></div>
                  <div className="min-w-0">
                    <div className="font-semibold">{t(lang, en, hi)}</div>
                    <div className="text-xs opacity-80 mt-0.5">{t(lang, enb, hib)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Services ----------
function Services({ lang }) {
  return (
    <section className="py-20" style={{ background: CREAM }} aria-labelledby="svc-head" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: TERRA }}>{t(lang, "Explore services", "सेवाएँ")}</div>
            <h2 id="svc-head" className="font-heading text-4xl sm:text-5xl mt-2 tracking-tight" style={{ color: FOREST }}>
              {t(lang, "Eight ways Anveshan helps you.", "आपकी मदद के आठ तरीके।")}
            </h2>
          </div>
          <Link to="/register" className="text-sm font-semibold inline-flex items-center gap-2" style={{ color: TERRA }}>
            {t(lang, "Join to unlock all services", "सभी सेवाओं के लिए जुड़ें")} <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="service-grid">
          {SERVICES.map((s) => {
            const Ic = s.icon;
            const [name, desc] = lang === "hi" ? s.hi : s.en;
            return (
              <Link key={s.to} to={s.to} data-testid={`svc-${s.to.replace("/", "")}`}
                className="group rounded-2xl p-5 bg-white border transition-all hover:-translate-y-0.5 hover:shadow-md"
                style={{ borderColor: "#E5D9CC" }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${FOREST}12`, color: FOREST }}>
                  <Ic size={24} weight="duotone" />
                </div>
                <div className="font-heading text-xl mt-3" style={{ color: INK }}>{name}</div>
                <p className="text-sm mt-1 leading-relaxed" style={{ color: "#5C564D" }}>{desc}</p>
                <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: TERRA }}>
                  {t(lang, "Explore", "देखें")} <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- Partners strip (verified training providers + partner orgs) ----------
function PartnersStrip({ lang }) {
  const [partners, setPartners] = useState(null);
  useEffect(() => {
    api.get("/public/partners").then(({ data }) => setPartners(data))
      .catch((err) => { console.error("partners failed", err); setPartners([]); });
  }, []);
  if (!partners || partners.length === 0) return null; // Hide entirely until partners exist
  const TYPE_LABELS = {
    training_provider: { en: "Training providers", hi: "प्रशिक्षण प्रदाता" },
    organization:      { en: "Partner organisations", hi: "सहयोगी संगठन" },
    sponsor:           { en: "Supporters", hi: "समर्थक" },
  };
  // group by type for a cleaner presentation if there are multiple categories
  const groups = partners.reduce((acc, p) => {
    const k = p.partner_type || "organization";
    (acc[k] = acc[k] || []).push(p);
    return acc;
  }, {});
  return (
    <section className="py-16" style={{ background: "#FDFBF6" }} aria-labelledby="partners-head" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: TERRA }}>
            {t(lang, "Our partners", "हमारे सहयोगी")}
          </div>
          <h2 id="partners-head" className="font-heading text-3xl sm:text-4xl mt-2 tracking-tight" style={{ color: FOREST }}>
            {t(lang, "Verified providers and allies.", "सत्यापित सहयोगी और सहायक।")}
          </h2>
          <p className="mt-2 text-sm" style={{ color: "#5C564D" }}>
            {t(lang,
              "Organisations and training providers that have been reviewed and verified by Anveshan.",
              "अन्वेषण द्वारा सत्यापित संगठन और प्रशिक्षण प्रदाता।")}
          </p>
        </div>
        <div className="mt-10 space-y-10" data-testid="partners-groups">
          {Object.entries(groups).map(([type, items]) => (
            <div key={type}>
              {Object.keys(groups).length > 1 && (
                <div className="text-xs tracking-widest uppercase font-bold mb-4 text-center" style={{ color: GOLD }}>
                  {t(lang, TYPE_LABELS[type]?.en || type, TYPE_LABELS[type]?.hi || type)}
                </div>
              )}
              <div className="flex items-center justify-center flex-wrap gap-6">
                {items.map((p) => {
                  const inner = (
                    <>
                      {p.logo_url ? (
                        <img src={p.logo_url} alt={p.name}
                          className="h-10 sm:h-12 w-auto object-contain max-w-[160px] grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition" />
                      ) : (
                        <div className="h-10 sm:h-12 px-4 flex items-center font-heading text-xl rounded-xl border"
                          style={{ color: FOREST, borderColor: "#E5D9CC", background: "white" }}>
                          {p.name}
                        </div>
                      )}
                    </>
                  );
                  const title = p.description || p.name;
                  return p.website ? (
                    <a key={p.id} href={p.website} target="_blank" rel="noreferrer noopener" title={title}
                      data-testid={`partner-${p.id}`} className="group flex items-center">
                      {inner}
                    </a>
                  ) : (
                    <div key={p.id} title={title} data-testid={`partner-${p.id}`} className="group flex items-center">
                      {inner}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- How it works ----------
function HowItWorks({ lang }) {
  return (
    <section className="py-20" aria-labelledby="how-head">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-3xl">
          <div className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: TERRA }}>{t(lang, "How it works", "कैसे काम करता है")}</div>
          <h2 id="how-head" className="font-heading text-4xl sm:text-5xl mt-2 tracking-tight" style={{ color: FOREST }}>
            {t(lang, "Six steps from joining to contributing.", "जुड़ने से योगदान तक छह कदम।")}
          </h2>
        </div>
        <ol className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="how-steps">
          {HOW_STEPS.map((s, i) => (
            <li key={i} className="rounded-2xl p-5 border bg-white relative overflow-hidden" style={{ borderColor: "#E5D9CC" }}>
              <div className="absolute -top-3 -right-3 w-20 h-20 rounded-full opacity-10" style={{ background: GOLD }} />
              <div className="font-heading text-4xl" style={{ color: FOREST }}>{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-2 font-semibold" style={{ color: INK }}>{t(lang, s.en, s.hi)}</div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ---------- Who Can Join ----------
function WhoCanJoin({ lang }) {
  return (
    <section className="py-20" style={{ background: "#FDFBF6" }} aria-labelledby="who-head">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-3xl">
          <div className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: TERRA }}>{t(lang, "Who can join", "कौन जुड़ सकता है")}</div>
          <h2 id="who-head" className="font-heading text-4xl sm:text-5xl mt-2 tracking-tight" style={{ color: FOREST }}>
            {t(lang, "Anveshan is for everyone.", "अन्वेषण सभी के लिए है।")}
          </h2>
        </div>
        <div className="mt-8 flex flex-wrap gap-2" data-testid="who-list">
          {WHO.map((w, i) => (
            <span key={i} className="rounded-full px-4 py-2 text-sm font-semibold bg-white border" style={{ borderColor: "#E5D9CC", color: INK }}>
              {t(lang, w.en, w.hi)}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Impact stats ----------
function ImpactStats({ lang }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data))
      .catch((err) => { console.error("stats failed", err); setStats({}); });
  }, []);
  const visibleStats = stats ? STAT_LABELS.filter((row) => (stats[row.key] || 0) > 0) : [];
  if (stats === null) return null;
  if (visibleStats.length === 0) return null; // hide the section entirely if nothing to show
  return (
    <section className="py-20" aria-labelledby="impact-head" data-testid="impact-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl">
          <div className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: TERRA }}>{t(lang, "Community impact", "सामुदायिक प्रभाव")}</div>
          <h2 id="impact-head" className="font-heading text-4xl sm:text-5xl mt-2 tracking-tight" style={{ color: FOREST }}>
            {t(lang, "Real numbers. No made-up figures.", "असली आँकड़े।")}
          </h2>
        </div>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="impact-grid">
          {visibleStats.map((row) => (
            <div key={row.key} className="rounded-2xl p-5 border bg-white text-center" style={{ borderColor: "#E5D9CC" }} data-testid={`stat-${row.key}`}>
              <div className="font-heading text-4xl" style={{ color: FOREST }}>{(stats[row.key] || 0).toLocaleString()}</div>
              <div className="text-xs tracking-wider uppercase mt-1" style={{ color: "#5C564D" }}>{t(lang, row.en, row.hi)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Join the movement CTA ----------
function JoinMovement({ lang }) {
  return (
    <section className="py-20" style={{ background: `linear-gradient(135deg, ${TERRA}10 0%, ${GOLD}18 100%)` }} aria-labelledby="join-head">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <div className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: TERRA }}>{t(lang, "Join the movement", "आंदोलन में जुड़ें")}</div>
        <h2 id="join-head" className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-2 tracking-tight" style={{ color: FOREST }}>
          {t(lang, "Your participation can make a difference.", "आपका एक कदम समाज में बदलाव ला सकता है।")}
        </h2>
        <p className="mt-4 text-lg max-w-2xl mx-auto" style={{ color: "#5C564D" }}>
          {t(lang,
            "A platform is only as strong as the people on it. Join Anveshan — contribute, hire, teach, help or simply be present.",
            "मंच की ताक़त उस पर जुड़े लोगों से बनती है। अन्वेषण से जुड़ें — योगदान दें, भर्ती करें, पढ़ाएँ, मदद करें।")}
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Link to="/register" data-testid="join-register" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white" style={{ background: TERRA }}>
            {t(lang, "Join Anveshan", "अन्वेषण से जुड़ें")} <ArrowRight size={14} weight="bold" />
          </Link>
          <Link to="/jobs" data-testid="join-opportunities" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold border-2" style={{ borderColor: FOREST, color: FOREST }}>
            {t(lang, "Explore opportunities", "अवसर देखें")}
          </Link>
          <Link to="/support" data-testid="join-support" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold" style={{ background: FOREST, color: CREAM }}>
            {t(lang, "Offer support", "सहायता प्रदान करें")} <HandHeart size={14} weight="fill" />
          </Link>
        </div>
      </div>
    </section>
  );
}
